import { useMemo } from "react";
import { deadlines } from "@lagerlog/core";
import type { Deadline } from "@lagerlog/core";
import { useCatalog, useLedger, useStore } from "../hooks.ts";
import { fmt } from "./Book.tsx";
import { SubNav } from "./SubNav.tsx";
import { IS_PANTRY, takeWords } from "../brand.ts";

const TITLE: Record<Deadline["state"], string> = {
  expired: "MHD abgelaufen",
  past: "Über dem Höhepunkt",
  soon: "MHD in den nächsten 4 Wochen",
  last: "Letztes Jahr im Trinkfenster",
  ready: "Jetzt trinken",
  young: "Noch zu jung",
};
const ORDER: Deadline["state"][] = ["expired", "past", "soon", "last", "ready", "young"];
const date = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;

/**
 * Fristen (docs/17-getraenke.md, S5): the drinking window of wines and the best-before date of
 * beer, juice and the like, in one list, most urgent first. Only what is in stock on this device.
 */
export function Deadlines() {
  const { store, version } = useStore();
  const catalog = useCatalog();
  const ledger = useLedger();
  const list = useMemo(() => {
    if (!catalog || !ledger) return [];
    const scope = new Set(store.config?.lagers ?? []);
    return deadlines(ledger, (id) => catalog.articles.get(id)?.fields, Date.now()).filter((d) => d.role === "ripeness" || scope.has(d.lager));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, ledger, version]);
  if (!catalog || !ledger) return null;
  // Where it lies: the storage, and its location where it has more than one („Kühlschrank“, „Bar · Kühlschrank“; B6).
  const place = (lager: string, location: string) => {
    const name = catalog.lagers.get(lager)?.name ?? lager;
    const several = [...catalog.locations.values()].filter((l) => l.lager === lager).length > 1;
    return several ? `${name} · ${catalog.locations.get(location)?.name ?? location}` : name;
  };
  const when = (d: Deadline) => (d.role === "expiry" ? `MHD ${date(d.date)} · ${place(d.lager, d.location)}` : `trinkreif ${d.from ?? "…"}–${d.to ?? "…"}`);
  return (
    <div className="deadlines">
      <SubNav current="fristen" />
      {list.length === 0 && <p className="muted">{IS_PANTRY ? "Keine Fristen: Was kein MHD hat, steht hier nicht." : "Keine Fristen: Weine ohne Trinkfenster und Getränke ohne MHD stehen hier nicht."}</p>}
      {ORDER.filter((s) => list.some((d) => d.state === s)).map((s) => (
        <section key={s} data-testid={`deadlines-${s}`}>
          <h3>{TITLE[s]}</h3>
          <ul className="list">
            {list
              .filter((d) => d.state === s)
              .map((d) => {
                const a = catalog.articles.get(d.article);
                return (
                  <li key={`${d.article}|${d.role === "expiry" ? d.lot : ""}`} className={`row between ${s === "expired" || s === "past" ? "negative" : ""}`}>
                    <span>
                      {a?.name ?? d.article}
                      <br />
                      <span className="muted">{when(d)}</span>
                    </span>
                    <span className="side">
                      {fmt(a, d.qty)
                        .split(" + ")
                        .map((part, i, all) => (
                          <span key={i}>{i < all.length - 1 ? `${part} +` : part}</span>
                        ))}
                    </span>
                  </li>
                );
              })}
          </ul>
        </section>
      ))}
      <p className="hint">{IS_PANTRY ? "Das MHD gibt man beim Einlagern an." : "Das Trinkfenster steht beim Wein (von–bis), das MHD gibt man beim Einlagern an."} Beim {takeWords(store.config?.kind).take} weist die App darauf hin, sperrt aber nie.</p>
    </div>
  );
}
